/**
 * Hoja 07 del paquete: Estado de Flujos de Efectivo, método indirecto.
 *
 * Cada rubro sale de la clasificación por actividad, no de números tecleados:
 * en la hoja de cálculo, «Compra de Activos Intangibles» era
 * `=460297536.55-16739.21+67359862.16` y en realidad eran criptoactivos.
 *
 * La conciliación es una identidad comprobable: la suma de los tres flujos
 * tiene que ser la variación real del efectivo. Se muestra la diferencia.
 *
 * El detalle por cuenta y la conciliación mes a mes son SUB-PESTAÑAS de esta
 * hoja. Antes eran dos entradas del menú, y una de ellas —`cash_flow_summary`—
 * se titulaba «Estado de Flujos de Efectivo» sin serlo: es la conciliación
 * mensual. Ese choque de rótulos era peor que una duplicación, porque desde el
 * menú no había forma de notarlo.
 */

import { Fragment } from "react";
import { Outlet } from "react-router-dom";
import { StatusBadge } from "../../components/StatusBadge";
import { BarrasSigno } from "../../components/viz/BarrasSigno";
import { Tabs } from "../../components/Tabs";
import { formatMoney, moneyClass } from "../../components/format";
import { isBalanced, useClosing } from "./closingContext";
import { ReportPanel } from "./ReportPanel";
import { StatementHead } from "./shared";

const SUB_PESTANAS = [
  { to: "/closing/cash-flow", label: "Estado oficial", end: true },
  { to: "/closing/cash-flow/detail", label: "Detalle por cuenta" },
  { to: "/closing/cash-flow/reconciliation", label: "Conciliación mensual" },
];

/** La hoja: encabezado, sub-pestañas y la vista elegida. */
export function CashFlowStatement() {
  const contexto = useClosing();
  const { package: pkg } = contexto;

  return (
    <>
      <StatementHead
        title="Estado de Flujos de Efectivo"
        subtitle={`del ${pkg.period_from} al ${pkg.period_to}`}
        homologation={pkg.homologation.cash_flow_statement}
      />

      <Tabs
        tabs={SUB_PESTANAS}
        variant="inline"
        label="Vistas del Estado de Flujos de Efectivo"
      />

      <Outlet context={contexto} />
    </>
  );
}

/** Detalle por cuenta del flujo, dentro de la hoja que explica. */
export function CashFlowDetail() {
  return (
    <ReportPanel
      reportKey="cash_flow"
      explanation="Las cuentas que componen cada rubro del estado. Sirve para
        responder de dónde sale un importe; el estado oficial es la pestaña
        anterior."
    />
  );
}

/**
 * Conciliación mes a mes.
 *
 * El estado oficial ya concilia el PERÍODO —flujo neto contra variación del
 * efectivo, con su diferencia—; lo que añade esta vista es el desglose mensual,
 * que localiza en qué mes se rompe una conciliación que no cuadra.
 */
export function CashFlowReconciliation() {
  return (
    <ReportPanel
      reportKey="cash_flow_summary"
      explanation="Conciliación mes a mes: flujo clasificado contra variación
        real del efectivo. La columna «Diferencia de conciliación» es un control
        contable y no se omite: si no es cero, la clasificación por actividad no
        explica el movimiento de ese mes."
    />
  );
}

/** El estado tal como se firma. */
export function CashFlowOfficial() {
  const { package: pkg } = useClosing();
  const estado = pkg.cash_flow;
  const cuadra = isBalanced(estado.reconciliation_difference);

  return (
    <>
      {/* Aquí iba un banner «Pendiente del contador» explicando que la
          clasificación por actividad la tiene que confirmar Contabilidad. Salía
          siempre —nunca estaba aprobada— así que ocupaba el primer bloque de la
          pantalla para decir algo que no cambiaba nunca. */}

      {/* El flujo por ACTIVIDAD, antes del renglón a renglón. Tres cifras que
          se comparan entre sí —cuánto entra por operación, cuánto sale por
          inversión— y que en una columna de importes obligan a restar de
          cabeza. Aquí el color dice el SIGNO y no la actividad, y el paréntesis
          de la cifra lo dice otra vez sin depender del color. */}
      <div className="card">
        <h2>Flujo por actividad</h2>
        <BarrasSigno
          tramos={estado.sections.map((section) => ({
            id: section.id,
            label: section.total_label,
            value: section.total,
          }))}
        />
      </div>

      <div className="card">
        <div className="table-wrap documento">
          <table>
            <thead>
              <tr>
                <th scope="col">Concepto</th>
                <th scope="col" className="num">
                  Importe
                </th>
              </tr>
            </thead>
            <tbody>
              {estado.sections.map((section) => (
                <Fragment key={section.id}>
                  <tr className="level-0">
                    <td colSpan={2}>{section.label}</td>
                  </tr>
                  {section.lines.map((line) => (
                    <tr key={`${section.id}-${line.label}`}>
                      <td style={{ paddingLeft: 26 }}>{line.label}</td>
                      <td className={moneyClass(line.amount)}>{formatMoney(line.amount)}</td>
                    </tr>
                  ))}
                  <tr className="level-1">
                    <td>{section.total_label}</td>
                    <td className={moneyClass(section.total)}>{formatMoney(section.total)}</td>
                  </tr>
                </Fragment>
              ))}
              {estado.reconciliation.map((line) => (
                <tr key={line.id} className="level-1">
                  <td>{line.label}</td>
                  <td className={moneyClass(line.amount)}>{formatMoney(line.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card banner">
        <StatusBadge tone={cuadra ? "good" : "critical"}>
          {cuadra ? "Conciliado" : "Sin conciliar"}
        </StatusBadge>
        <div className="banner-body">
          <strong>
            Flujo neto {formatMoney(estado.net_flow)} contra variación del efectivo{" "}
            {formatMoney(estado.cash_variation)}
          </strong>
          <p className="small muted">
            Diferencia {formatMoney(estado.reconciliation_difference)}. Si no es
            cero, la clasificación por actividad no explica el movimiento del
            efectivo y el estado no está terminado.
          </p>
        </div>
      </div>
    </>
  );
}
